import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const ADMIN_ID = import.meta.env.VITE_ADMIN_USER_ID || "";

const css = `
  .admin-shell { min-height:100vh; display:flex; flex-direction:column; background:var(--bg); }

  /* NAV */
  .admin-nav { display:flex; align-items:center; justify-content:space-between; padding:14px 28px; background:var(--surface); border-bottom:1px solid var(--border); position:sticky; top:0; z-index:100; }
  .admin-nav-logo { font-family:var(--head); font-size:20px; font-weight:800; letter-spacing:2px; }
  .admin-nav-logo span { color:var(--blue); }
  .admin-badge { background:rgba(239,68,68,.15); color:var(--danger); border:1px solid rgba(239,68,68,.3); border-radius:6px; padding:3px 10px; font-size:11px; font-weight:700; letter-spacing:1px; }
  .admin-nav-tabs { display:flex; gap:4px; background:var(--card); border-radius:10px; padding:3px; }
  .admin-tab { padding:7px 18px; border-radius:7px; border:none; cursor:pointer; font-family:var(--body); font-size:13px; font-weight:500; transition:all .2s; background:transparent; color:var(--muted); }
  .admin-tab.active { background:var(--blue); color:#fff; }

  /* CONTENT */
  .admin-content { flex:1; padding:28px; max-width:1300px; width:100%; margin:0 auto; }

  /* PANELS */
  .panel { background:var(--card); border:1px solid var(--border); border-radius:16px; overflow:hidden; margin-bottom:0; }
  .panel-header { padding:16px 20px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; }
  .panel-header h3 { font-family:var(--head); font-size:18px; font-weight:700; letter-spacing:1px; }

  /* BADGES */
  .badge { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; letter-spacing:.5px; }
  .badge.confirmed { background:rgba(34,197,94,.12); color:var(--success); }
  .badge.pending   { background:rgba(245,158,11,.12); color:var(--warn); }
  .badge.declined  { background:rgba(239,68,68,.12);  color:var(--danger); }
  .badge.cancelled { background:rgba(239,68,68,.12);  color:var(--danger); }
  .badge.completed { background:rgba(0,201,167,.12);  color:var(--teal); }
  .badge.pro  { background:rgba(59,130,246,.15); color:var(--blue); border:1px solid rgba(59,130,246,.3); }
  .badge.free { background:rgba(100,116,139,.12); color:var(--muted); }
  .admin-stats { display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:14px; margin-bottom:28px; }
  .stat-card  { background:var(--card); border:1px solid var(--border); border-radius:14px; padding:20px; }
  .stat-label { font-size:11px; font-weight:700; letter-spacing:2px; color:var(--muted); text-transform:uppercase; margin-bottom:8px; }
  .stat-val   { font-family:var(--head); font-size:36px; font-weight:800; }
  .stat-val.blue  { color:var(--blue); }
  .stat-val.teal  { color:var(--teal); }
  .stat-val.warn  { color:var(--warn); }
  .stat-sub   { color:var(--muted); font-size:12px; margin-top:4px; }

  /* TABLE */
  .admin-table { width:100%; border-collapse:collapse; }
  .admin-table th { text-align:left; font-size:11px; font-weight:700; letter-spacing:2px; color:var(--muted); text-transform:uppercase; padding:10px 16px; border-bottom:1px solid var(--border); white-space:nowrap; }
  .admin-table td { padding:13px 16px; border-bottom:1px solid var(--border); font-size:14px; vertical-align:middle; }
  .admin-table tr:last-child td { border-bottom:none; }
  .admin-table tr:hover td { background:var(--card2); }
  .admin-table td.muted { color:var(--muted); font-size:12px; }

  /* DETAILER ROW */
  .det-avatar { width:36px; height:36px; border-radius:50%; background:linear-gradient(135deg,var(--blue),var(--teal)); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:13px; color:#fff; flex-shrink:0; }

  /* SEARCH */
  .admin-search { background:var(--card2); border:1px solid var(--border); border-radius:10px; padding:10px 14px; color:var(--text); font-family:var(--body); font-size:14px; outline:none; width:280px; }
  .admin-search:focus { border-color:var(--blue); }

  /* MODAL */
  .admin-modal { background:var(--surface); border:1px solid var(--border); border-radius:18px; padding:28px; max-width:520px; width:100%; max-height:88vh; overflow-y:auto; }
  .admin-modal h3 { font-family:var(--head); font-size:22px; font-weight:800; letter-spacing:1px; margin-bottom:20px; }
  .detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:20px; }
  .detail-cell { background:var(--card2); border:1px solid var(--border); border-radius:10px; padding:12px 14px; }
  .detail-cell-label { font-size:11px; font-weight:700; color:var(--muted); letter-spacing:1px; text-transform:uppercase; margin-bottom:4px; }
  .detail-cell-val { font-size:14px; font-weight:600; word-break:break-all; }

  /* PLAN OVERRIDE */
  .plan-override { display:flex; gap:8px; }
  .plan-btn { flex:1; padding:9px; border-radius:8px; border:1px solid var(--border); background:var(--card2); color:var(--muted); font-family:var(--body); font-size:13px; font-weight:500; cursor:pointer; transition:.2s; }
  .plan-btn:hover { border-color:var(--blue); color:var(--text); }
  .plan-btn.active-free { border-color:var(--success); color:var(--success); background:rgba(34,197,94,.08); }
  .plan-btn.active-pro  { border-color:var(--blue);    color:var(--blue);    background:rgba(59,130,246,.08); }

  /* CHART */
  .bar-chart { display:flex; align-items:flex-end; gap:6px; height:120px; padding:0 4px; }
  .bar-col { display:flex; flex-direction:column; align-items:center; flex:1; gap:4px; }
  .bar-fill { width:100%; background:var(--blue); border-radius:4px 4px 0 0; transition:height .4s ease; min-height:2px; }
  .bar-label { font-size:10px; color:var(--muted); white-space:nowrap; }
  .bar-val { font-size:10px; color:var(--teal); font-weight:600; }

  /* ACCESS DENIED */
  .access-denied { min-height:100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; }
  .access-denied h2 { font-family:var(--head); font-size:40px; font-weight:800; color:var(--danger); }
  .access-denied p { color:var(--muted); font-size:16px; }

  /* ── MOBILE ─────────────────────────────────────────── */
  @media (max-width: 640px) {
    .admin-nav { padding:12px 14px; flex-wrap:wrap; gap:8px; }
    .admin-nav-tabs { order:3; width:100%; justify-content:center; }
    .admin-tab { padding:6px 12px; font-size:12px; }
    .admin-content { padding:16px 12px 32px; }
    .admin-stats { grid-template-columns:1fr 1fr; gap:10px; }
    .stat-card { padding:14px; }
    .stat-val { font-size:26px; }
    /* Charts stack on mobile */
    .charts-row { grid-template-columns:1fr !important; }
    /* Tables: scroll horizontally on mobile */
    .admin-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
    .admin-table { min-width:520px; }
    .admin-table td, .admin-table th { padding:10px 10px; }
    /* Charts */
    .bar-chart { height:80px; }
    .bar-val { font-size:9px; }
    .bar-label { font-size:9px; }
    /* Detail grid: single column */
    .detail-grid { grid-template-columns:1fr; }
    /* Hide less important columns on mobile */
    .admin-table .col-hide-mobile { display:none; }
  }
`;

const fmt$ = n => `$${Number(n||0).toLocaleString()}`;
const initials = name => name?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() || "?";
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "—";

export default function AdminPanel({ session }) {
  const navigate = useNavigate();
  const [tab, setTab]             = useState("overview");
  const [detailers, setDetailers] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [selected, setSelected]   = useState(null);
  const [updating, setUpdating]   = useState(false);
  const [toast, setToast]         = useState(null);

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  // ── GUARD ──────────────────────────────────────────
  if (session?.user?.id !== ADMIN_ID) {
    return (
      <>
        <style>{css}</style>
        <div className="access-denied">
          <span style={{ fontSize:56 }}>🚫</span>
          <h2>ACCESS DENIED</h2>
          <p>You don't have permission to view this page.</p>
          <button className="btn-primary" onClick={() => navigate("/dashboard")}>← Back to Dashboard</button>
        </div>
      </>
    );
  }

  // ── LOAD ───────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    const [{ data: dets }, { data: bkgs }] = await Promise.all([
      supabase.from("detailers").select("*").order("created_at", { ascending: false }),
      supabase.from("bookings").select("detailer_id, amount, status, created_at, name, email").order("created_at", { ascending: false }),
    ]);
    setDetailers(dets ?? []);
    setAllBookings(bkgs ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── PLAN OVERRIDE ──────────────────────────────────
  const setPlan = async (detailerId, plan) => {
    setUpdating(true);
    await supabase.from("detailers").update({
      plan,
      subscription_status: plan === "pro" ? "active" : "inactive",
      plan_expires_at: plan === "pro" ? new Date(Date.now() + 365*24*60*60*1000).toISOString() : null,
    }).eq("id", detailerId);
    setDetailers(prev => prev.map(d => d.id === detailerId ? { ...d, plan } : d));
    if (selected?.id === detailerId) setSelected(s => ({ ...s, plan }));
    showToast(`Plan updated to ${plan.toUpperCase()} ✓`);
    setUpdating(false);
  };

  // ── COMPUTED ───────────────────────────────────────
  const totalRevenue  = allBookings.filter(b => b.status !== "declined" && b.status !== "cancelled").reduce((a,b) => a+(b.amount||0), 0);
  const proCount      = detailers.filter(d => d.plan === "pro").length;
  const freeCount     = detailers.filter(d => d.plan !== "pro").length;
  const mrrEstimate   = proCount * 29;
  const thisMonth     = new Date(); thisMonth.setDate(1); thisMonth.setHours(0,0,0,0);
  const newThisMonth  = detailers.filter(d => new Date(d.created_at) >= thisMonth).length;

  // Signups by month (last 6)
  const signupsByMonth = (() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i); d.setDate(1); d.setHours(0,0,0,0);
      const next = new Date(d); next.setMonth(next.getMonth() + 1);
      months.push({
        label: d.toLocaleString("en-US", { month: "short" }),
        count: detailers.filter(det => { const c = new Date(det.created_at); return c >= d && c < next; }).length,
      });
    }
    return months;
  })();

  const maxSignups = Math.max(...signupsByMonth.map(m => m.count), 1);

  // Revenue by month (last 6)
  const revenueByMonth = (() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i); d.setDate(1); d.setHours(0,0,0,0);
      const next = new Date(d); next.setMonth(next.getMonth() + 1);
      months.push({
        label: d.toLocaleString("en-US", { month: "short" }),
        val: allBookings.filter(b => {
          const c = new Date(b.created_at);
          return c >= d && c < next && b.status !== "declined" && b.status !== "cancelled";
        }).reduce((a,b) => a+(b.amount||0), 0),
      });
    }
    return months;
  })();

  const maxRevenue = Math.max(...revenueByMonth.map(m => m.val), 1);

  // Per-detailer booking stats
  const detailerStats = detailers.map(d => {
    const bkgs = allBookings.filter(b => b.detailer_id === d.id);
    const rev = bkgs.filter(b => b.status !== "declined" && b.status !== "cancelled").reduce((a,b)=>a+(b.amount||0),0);
    return { ...d, bookingCount: bkgs.length, revenue: rev };
  });

  const filtered = detailerStats.filter(d =>
    !search ||
    d.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    d.email?.toLowerCase().includes(search.toLowerCase()) ||
    d.slug?.toLowerCase().includes(search.toLowerCase()) ||
    d.city?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <style>{css}</style>
      <div className="admin-shell">

        {/* NAV */}
        <nav className="admin-nav">
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div className="admin-nav-logo">NEO<span> DETAIL</span></div>
            <span className="admin-badge">ADMIN</span>
          </div>
          <div className="admin-nav-tabs">
            {[["overview","📊 Overview"],["detailers","👥 Detailers"],["bookings","📋 Bookings"]].map(([id,label]) => (
              <button key={id} className={`admin-tab ${tab===id?"active":""}`} onClick={()=>setTab(id)}>{label}</button>
            ))}
          </div>
          <button className="btn-ghost" onClick={() => navigate("/dashboard")}>← Dashboard</button>
        </nav>

        <div className="admin-content fade-in">

          {/* ── OVERVIEW ─────────────────────────────── */}
          {tab === "overview" && (
            <>
              <div style={{ marginBottom:24 }}>
                <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>PLATFORM OVERVIEW</h2>
                <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>NEO Detail · Admin</div>
              </div>

              {/* Stats */}
              <div className="admin-stats">
                {[
                  ["Total Detailers",  detailers.length,         "blue"],
                  ["Pro Accounts",     proCount,                  "teal"],
                  ["Free Accounts",    freeCount,                 ""],
                  ["New This Month",   newThisMonth,              "warn"],
                  ["Est. MRR",         fmt$(mrrEstimate),         "teal"],
                  ["Platform Revenue", fmt$(totalRevenue),        "teal"],
                ].map(([label, val, color]) => (
                  <div className="stat-card" key={label}>
                    <div className="stat-label">{label}</div>
                    <div className={`stat-val ${color}`}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Charts row */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }} className="charts-row">
                {/* Signups chart */}
                <div className="panel">
                  <div className="panel-header"><h3>NEW SIGNUPS</h3><span style={{ fontSize:12, color:"var(--muted)" }}>Last 6 months</span></div>
                  <div style={{ padding:"20px 16px 12px" }}>
                    <div className="bar-chart">
                      {signupsByMonth.map(m => (
                        <div className="bar-col" key={m.label}>
                          <div className="bar-val">{m.count || ""}</div>
                          <div className="bar-fill" style={{ height:`${(m.count/maxSignups)*100}%` }} />
                          <div className="bar-label">{m.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Revenue chart */}
                <div className="panel">
                  <div className="panel-header"><h3>BOOKING REVENUE</h3><span style={{ fontSize:12, color:"var(--muted)" }}>Last 6 months</span></div>
                  <div style={{ padding:"20px 16px 12px" }}>
                    <div className="bar-chart">
                      {revenueByMonth.map(m => (
                        <div className="bar-col" key={m.label}>
                          <div className="bar-val">{m.val > 0 ? `$${m.val}` : ""}</div>
                          <div className="bar-fill" style={{ height:`${(m.val/maxRevenue)*100}%`, background:"var(--teal)" }} />
                          <div className="bar-label">{m.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Top detailers by revenue */}
              <div className="panel">
                <div className="panel-header"><h3>TOP DETAILERS BY REVENUE</h3></div>
                <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Business</th>
                      <th>Plan</th>
                      <th className="col-hide-mobile">Bookings</th>
                      <th>Revenue</th>
                      <th className="col-hide-mobile">Location</th>
                      <th className="col-hide-mobile">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...detailerStats].sort((a,b) => b.revenue-a.revenue).slice(0,10).map(d => (
                      <tr key={d.id} style={{ cursor:"pointer" }} onClick={() => { setSelected(d); setTab("detailers"); }}>
                        <td>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <div className="det-avatar">{initials(d.business_name)}</div>
                            <div>
                              <div style={{ fontWeight:600 }}>{d.business_name}</div>
                              <div style={{ fontSize:11, color:"var(--muted)" }}>{d.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className={`badge ${d.plan === "pro" ? "confirmed" : "pending"}`}>{d.plan?.toUpperCase()}</span></td>
                        <td className="muted col-hide-mobile">{d.bookingCount}</td>
                        <td style={{ fontFamily:"var(--head)", color:"var(--teal)", fontWeight:700 }}>{fmt$(d.revenue)}</td>
                        <td className="muted col-hide-mobile">{d.city ? `${d.city}, ${d.state}` : "—"}</td>
                        <td className="muted col-hide-mobile">{fmtDate(d.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </>
          )}

          {/* ── DETAILERS ─────────────────────────────── */}
          {tab === "detailers" && (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
                <div>
                  <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>ALL DETAILERS</h2>
                  <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>{detailers.length} total · {proCount} Pro · {freeCount} Free</div>
                </div>
                <input className="admin-search" placeholder="Search by name, email, city..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>

              {selected ? (
                /* ── DETAILER DETAIL VIEW ── */
                <div className="panel fade-in">
                  <div className="panel-header">
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <button className="btn-edit" onClick={() => setSelected(null)}>← All Detailers</button>
                      <h3>{selected.business_name?.toUpperCase()}</h3>
                      <span className={`badge ${selected.plan === "pro" ? "confirmed" : "pending"}`}>{selected.plan?.toUpperCase()}</span>
                    </div>
                    <a href={`/book/${selected.slug}`} target="_blank" rel="noreferrer">
                      <button className="btn-edit" style={{ fontSize:12 }}>View Page →</button>
                    </a>
                  </div>
                  <div style={{ padding:20 }}>
                    <div className="detail-grid">
                      {[
                        ["Email",        selected.email || "—"],
                        ["Phone",        selected.phone || "—"],
                        ["Location",     selected.city ? `${selected.city}, ${selected.state}` : "—"],
                        ["Slug",         selected.slug],
                        ["Joined",       fmtDate(selected.created_at)],
                        ["Plan",         selected.plan?.toUpperCase()],
                        ["Sub Status",   selected.subscription_status || "—"],
                        ["Stripe ID",    selected.stripe_customer_id || "—"],
                        ["Stripe Acct",  selected.stripe_account_id || "—"],
                        ["Connected",    selected.stripe_connected ? "Yes" : "No"],
                        ["Bookings",     selected.bookingCount],
                        ["Revenue",      fmt$(selected.revenue)],
                      ].map(([k,v]) => (
                        <div className="detail-cell" key={k}>
                          <div className="detail-cell-label">{k}</div>
                          <div className="detail-cell-val">{v}</div>
                        </div>
                      ))}
                    </div>

                    {/* Bio */}
                    {selected.bio && (
                      <div style={{ background:"var(--card2)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 14px", marginBottom:16 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Bio</div>
                        <div style={{ fontSize:13, color:"var(--muted)", lineHeight:1.6 }}>{selected.bio}</div>
                      </div>
                    )}

                    {/* Plan override */}
                    <div style={{ marginBottom:20 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Override Plan</div>
                      <div className="plan-override">
                        <button className={`plan-btn ${selected.plan !== "pro" ? "active-free" : ""}`}
                          onClick={() => setPlan(selected.id, "free")} disabled={updating}>
                          FREE
                        </button>
                        <button className={`plan-btn ${selected.plan === "pro" ? "active-pro" : ""}`}
                          onClick={() => setPlan(selected.id, "pro")} disabled={updating}>
                          PRO
                        </button>
                      </div>
                      <div style={{ fontSize:11, color:"var(--muted)", marginTop:6 }}>Manually override this detailer's plan. Use for trials, comps, or fixing billing issues.</div>
                    </div>

                    {/* Recent bookings */}
                    <div style={{ fontSize:12, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Recent Bookings</div>
                    {allBookings.filter(b => b.detailer_id === selected.id).slice(0,5).length === 0 ? (
                      <div style={{ color:"var(--muted)", fontSize:13 }}>No bookings yet.</div>
                    ) : (
                      <table className="admin-table">
                        <thead><tr><th>Date</th><th>Amount</th><th>Status</th></tr></thead>
                        <tbody>
                          {allBookings.filter(b => b.detailer_id === selected.id).slice(0,5).map((b,i) => (
                            <tr key={i}>
                              <td className="muted">{fmtDate(b.created_at)}</td>
                              <td style={{ fontFamily:"var(--head)", color:"var(--teal)", fontWeight:700 }}>{fmt$(b.amount)}</td>
                              <td><span className={`badge ${b.status}`}>{b.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              ) : (
                /* ── DETAILER LIST ── */
                <div className="panel">
                  <div style={{ overflowX:"auto" }} className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Business</th>
                          <th>Plan</th>
                          <th className="col-hide-mobile">Location</th>
                          <th className="col-hide-mobile">Bookings</th>
                          <th>Revenue</th>
                          <th className="col-hide-mobile">Stripe</th>
                          <th className="col-hide-mobile">Joined</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={8} style={{ textAlign:"center", padding:40 }}><span className="spinner" style={{ borderTopColor:"var(--blue)" }}/></td></tr>
                        ) : filtered.length === 0 ? (
                          <tr><td colSpan={8} style={{ textAlign:"center", padding:40, color:"var(--muted)" }}>No detailers found</td></tr>
                        ) : filtered.map(d => (
                          <tr key={d.id} style={{ cursor:"pointer" }} onClick={() => setSelected(d)}>
                            <td>
                              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                <div className="det-avatar">{initials(d.business_name)}</div>
                                <div>
                                  <div style={{ fontWeight:600 }}>{d.business_name}</div>
                                  <div style={{ fontSize:11, color:"var(--muted)" }}>{d.email}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className={`badge ${d.plan === "pro" ? "confirmed" : "pending"}`}>{d.plan?.toUpperCase()}</span></td>
                            <td className="muted col-hide-mobile">{d.city ? `${d.city}, ${d.state}` : "—"}</td>
                            <td className="muted col-hide-mobile">{d.bookingCount}</td>
                            <td style={{ fontFamily:"var(--head)", color:"var(--teal)", fontWeight:700 }}>{fmt$(d.revenue)}</td>
                            <td className="col-hide-mobile">
                              {d.stripe_connected
                                ? <span style={{ color:"var(--success)", fontSize:12 }}>✓ Connected</span>
                                : <span style={{ color:"var(--muted)", fontSize:12 }}>—</span>}
                            </td>
                            <td className="muted col-hide-mobile">{fmtDate(d.created_at)}</td>
                            <td>
                              <button className="btn-edit" style={{ fontSize:12 }}
                                onClick={e => { e.stopPropagation(); setSelected(d); }}>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── BOOKINGS (platform-wide) ──────────────── */}
          {tab === "bookings" && (
            <>
              <div style={{ marginBottom:24 }}>
                <h2 style={{ fontFamily:"var(--head)", fontSize:28, fontWeight:800, letterSpacing:2 }}>ALL BOOKINGS</h2>
                <div style={{ color:"var(--muted)", fontSize:13, marginTop:4 }}>{allBookings.length} total across all detailers</div>
              </div>

              {/* Quick stats */}
              <div className="admin-stats" style={{ marginBottom:20 }}>
                {[
                  ["Total",     allBookings.length,                                                           "blue"],
                  ["Confirmed", allBookings.filter(b=>b.status==="confirmed").length,                         ""],
                  ["Pending",   allBookings.filter(b=>b.status==="pending").length,                           "warn"],
                  ["Completed", allBookings.filter(b=>b.status==="completed").length,                         "teal"],
                  ["Declined",  allBookings.filter(b=>b.status==="declined"||b.status==="cancelled").length,  ""],
                  ["Revenue",   fmt$(totalRevenue),                                                           "teal"],
                ].map(([label,val,color]) => (
                  <div className="stat-card" key={label}>
                    <div className="stat-label">{label}</div>
                    <div className={`stat-val ${color}`}>{val}</div>
                  </div>
                ))}
              </div>

              <div className="panel">
                <div style={{ overflowX:"auto" }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Detailer</th>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={5} style={{ textAlign:"center", padding:40 }}><span className="spinner" style={{ borderTopColor:"var(--blue)" }}/></td></tr>
                      ) : allBookings.slice(0, 100).map((b, i) => {
                        const det = detailers.find(d => d.id === b.detailer_id);
                        return (
                          <tr key={i}>
                            <td>
                              <div style={{ fontWeight:500, fontSize:13 }}>{det?.business_name || "—"}</div>
                              <div style={{ fontSize:11, color:"var(--muted)" }}>{det?.slug || "—"}</div>
                            </td>
                            <td>
                              <div style={{ fontWeight:500, fontSize:13 }}>{b.name || "—"}</div>
                              <div style={{ fontSize:11, color:"var(--muted)" }}>{b.email || ""}</div>
                            </td>
                            <td style={{ fontFamily:"var(--head)", color:"var(--teal)", fontWeight:700 }}>{fmt$(b.amount)}</td>
                            <td><span className={`badge ${b.status}`}>{b.status}</span></td>
                            <td className="muted">{fmtDate(b.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
